import type { FlowSensorDto } from '@shelflix/trpc/routes/flowSensor/list/useCase';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { PERIOD_PRESETS, daysAgoBr, todayBr, type Period } from './format';

export type FlowSensorFilterValue = Period & { sensorUuid: string; facilityUuid: string };

interface Props {
  value: FlowSensorFilterValue;
  onChange: (value: FlowSensorFilterValue) => void;
  sensors: FlowSensorDto[];
}

// Filtros numa linha acima dos gráficos: período (atalhos ou datas) + sensor + estabelecimento.
export function FlowSensorFilter({ value, onChange, sensors }: Props) {
  const facilities = Array.from(
    new Map(sensors.flatMap((s) => (s.facility ? [[s.facility.uuid, s.facility.name] as const] : []))).entries()
  );
  const isPreset = (days: number) => value.from === daysAgoBr(days) && value.to === todayBr();

  return (
    <div className="flex w-full flex-wrap items-end gap-3">
      <div className="flex gap-1 rounded-md border p-1">
        {PERIOD_PRESETS.map((p) => (
          <Button
            key={p.label}
            size="sm"
            variant={isPreset(p.days) ? 'default' : 'ghost'}
            onClick={() => onChange({ ...value, from: daysAgoBr(p.days), to: todayBr() })}
          >
            {p.label}
          </Button>
        ))}
      </div>
      <label className="flex flex-col gap-1 text-xs text-[var(--gray-secondary)]">
        De
        <Input
          type="date"
          className="h-9 w-[150px]"
          value={value.from}
          max={value.to}
          onChange={(e) => e.target.value && onChange({ ...value, from: e.target.value })}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-[var(--gray-secondary)]">
        Até
        <Input
          type="date"
          className="h-9 w-[150px]"
          value={value.to}
          min={value.from}
          max={todayBr()}
          onChange={(e) => e.target.value && onChange({ ...value, to: e.target.value })}
        />
      </label>
      <Select value={value.sensorUuid} onValueChange={(sensorUuid) => onChange({ ...value, sensorUuid })}>
        <SelectTrigger className="h-9 w-[220px]">
          <SelectValue placeholder="Sensor" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos os sensores</SelectItem>
          {sensors.map((s) => (
            <SelectItem key={s.uuid} value={s.uuid}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {facilities.length > 0 && (
        <Select value={value.facilityUuid} onValueChange={(facilityUuid) => onChange({ ...value, facilityUuid })}>
          <SelectTrigger className="h-9 w-[220px]">
            <SelectValue placeholder="Estabelecimento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os estabelecimentos</SelectItem>
            {facilities.map(([uuid, name]) => (
              <SelectItem key={uuid} value={uuid}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

export const toPeriodInput = (f: FlowSensorFilterValue) => ({
  from: f.from,
  to: f.to,
  ...(f.sensorUuid !== 'all' && { sensorUuids: [f.sensorUuid] }),
  ...(f.facilityUuid !== 'all' && { facilityUuid: f.facilityUuid }),
});
